import { useIonRouter } from '@ionic/react';
import type { Workspace } from '@up2stay/up2promo-api-client';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shellPaths } from '@/core/router/app-paths';

import { CreateWorkspaceWizard } from '@/core/components/onboarding/create-workspace-wizard';
import { OnboardingLayout } from '@/core/components/onboarding/onboarding-layout';
import {
  useWorkspaceBootstrapEffects,
  useWorkspaceBootstrapStatus,
} from '@/core/hooks/use-workspace-bootstrap';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import {
  useSelectedWorkspaceId,
  useWorkspaceStore,
} from '@/core/store/workspace.store';

/**
 * Mandatory onboarding for zero workspaces, and resume when a workspace
 * exists but plan/trial was never confirmed. Starts at Intent (no separate
 * welcome gate). Create-another uses `CreateWorkspacePage` at
 * `/tabs/more/workspace/create`.
 */
export function OnboardingPage() {
  useWorkspaceBootstrapEffects();
  const router = useIonRouter();
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  const bootstrapStatus = useWorkspaceBootstrapStatus();
  const { workspaces } = useWorkspaces();
  const selectedWorkspaceId = useSelectedWorkspaceId();

  const resumeWorkspace = useMemo((): Workspace | null => {
    if (workspaces.length === 0) return null;
    return (
      workspaces.find((ws) => ws.id === selectedWorkspaceId) ??
      workspaces[0] ??
      null
    );
  }, [workspaces, selectedWorkspaceId]);

  const isPlanResume =
    bootstrapStatus === 'needs-onboarding' && resumeWorkspace != null;

  // Stick on the wizard after first paint so membership/plan-gate refetch
  // does not remount Intent behind a spinner.
  const [wizardReady, setWizardReady] = useState(
    () => bootstrapStatus === 'needs-onboarding',
  );
  if (bootstrapStatus === 'needs-onboarding' && !wizardReady) {
    setWizardReady(true);
  }

  useEffect(() => {
    if (!wizardReady && bootstrapStatus === 'loading') return;
    if (bootstrapStatus === 'loading') return;
    if (bootstrapStatus === 'needs-onboarding') return;
    // Plan gate still settling after create can briefly look "ready" while the
    // wizard is mid-flow; only leave once bootstrap is stably ready.
    router.push(shellPaths.home, 'root', 'replace');
  }, [wizardReady, bootstrapStatus, router]);

  const handleCreated = useCallback(
    (workspace: Workspace) => {
      setWorkspace(workspace);
      router.push(shellPaths.home, 'root', 'replace');
    },
    [router, setWorkspace],
  );

  const showInitialLoader = bootstrapStatus === 'loading' && !wizardReady;

  return (
    <OnboardingLayout>
      {showInitialLoader ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <CreateWorkspaceWizard
          mode="onboarding"
          variant="page"
          onCreated={handleCreated}
          resumeWorkspace={isPlanResume ? resumeWorkspace : null}
        />
      )}
    </OnboardingLayout>
  );
}
